import React, { useState } from "react";
import { 
    ThumbsUp, 
    ThumbsDown, 
    Share2, 
    Ellipsis, 
    Eye, 
    Bell,
    MessageSquare
} from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";



function ActionButtons() {
    return (
        <div data-section-id="37288" className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 no-scrollbar">
            <div className="flex items-center rounded-sm bg-muted/50 dark:bg-secondary/20">
                <Button variant="ghost" size="sm" className="rounded-l-sm px-4 hover:bg-accent border-r border-border/50 font-sans text-foreground/80">
                    <ThumbsUp className="mr-2 h-4 w-4" />
                    Helpful
                </Button>
                <Button variant="ghost" size="sm" className="rounded-r-sm px-4 hover:bg-accent font-sans text-foreground/80">
                    <ThumbsDown className="h-4 w-4" />
                </Button>
            </div>
            
            <Button variant="ghost" size="sm" className="rounded-sm bg-muted/50 hover:bg-accent dark:bg-secondary/20 px-4 font-sans text-foreground/80">
                <Share2 className="mr-2 h-4 w-4" />
                Share Analysis
            </Button>
            
            <Button variant="ghost" size="sm" className="rounded-sm bg-muted/50 hover:bg-accent dark:bg-secondary/20 px-4">
                <Ellipsis className="h-4 w-4" />
            </Button>
        </div>
    );
}



function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
    const { posts, loading, error } = usePosts();

    if (loading) {
        return (
            <div className="flex flex-col gap-4">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex gap-2 animate-pulse" data-index={i}>
                        <div className="w-40 h-24 bg-muted rounded-sm shrink-0" />
                        <div className="flex flex-col gap-2 w-full">
                            <div className="h-4 bg-muted rounded w-3/4" />
                            <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error || posts.length === 0) return null;

    
    const filteredPosts = posts.filter(p => p.id !== currentPostId);

    return (
        <div className="flex flex-col gap-4">
            {filteredPosts.map((post, i) => (
                <Link key={post.id} to={post.link || "#"} className="group flex gap-3 items-start" data-index={i}>
                    <div className="relative w-40 shrink-0 aspect-video rounded-sm overflow-hidden bg-muted shadow-sm">
                        {post.featuredImage ? (
                            <img 
                                src={post.featuredImage} 
                                alt={post.title} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-accent/30 text-muted-foreground">
                                <Eye className="h-6 w-6" />
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-1 min-w-0">
                        <h3 className="text-sm font-serif font-bold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                            {post.title}
                        </h3>
                        <div className="text-xs font-sans text-muted-foreground">
                            {post.author?.name || "AOTO Strategic Advisor"}
                        </div>
                        <div className="text-xs font-sans text-muted-foreground flex items-center gap-1">
                            <span>1.2K readers</span>
                            <span>•</span>
                            <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recently"}</span>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}

function RelatedPostsSidebar({ currentPostId }: { currentPostId: string }) {
    
    
    const relatedQuery = new WP_Query({
        per_page: 10,
        orderby: "date",
        order: "desc",
    });

    return (
        <div className="w-full lg:w-[350px] shrink-0 flex flex-col gap-4">
            <h3 className="font-serif font-bold text-lg px-1 text-primary tracking-tight">Industry Insights</h3>
            <PostsProvider wp_query={{ per_page: 10, orderby: "date", order: "desc" }}>
                <RelatedPostsConsumer currentPostId={currentPostId} />
            </PostsProvider>
        </div>
    );
}



function SinglePostConsumer() {
    const { posts, loading, error, refetch } = usePosts();
    const [isSubscribed, setIsSubscribed] = useState(false);

    if (loading) {
        return (
            <div className="w-full max-w-screen-xl mx-auto p-4 md:p-6 grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-6">
                <div className="space-y-4">
                    <div className="w-full aspect-video bg-muted rounded-sm animate-pulse" />
                    <div className="h-8 bg-muted rounded w-2/3 animate-pulse" />
                    <div className="h-12 bg-muted rounded w-full animate-pulse" />
                </div>
                <div className="hidden lg:block space-y-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-24 bg-muted rounded-sm animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full p-12 text-center text-destructive">
                <h2 className="text-xl font-serif font-bold">Error loading analysis</h2>
                <p className="mb-4 font-sans">{error}</p>
                <Button onClick={() => refetch()} className="rounded-sm">Retry Connection</Button>
            </div>
        );
    }

    if (!posts || posts.length === 0) {
        return (
            <div className="w-full p-12 text-center text-muted-foreground">
                <h2 className="text-xl font-serif font-bold">Report not found</h2>
                <p className="font-sans">The strategic content you are looking for does not exist.</p>
            </div>
        );
    }

    const post = posts[0];

    return (
        <div className="w-full max-w-[1800px] mx-auto p-4 md:p-6 grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-6">
            {}
            <div className="flex flex-col gap-4 min-w-0">
                {}
                <div className="w-full aspect-video bg-primary rounded-sm overflow-hidden shadow-lg relative group">
                    {post.featuredImage ? (
                        <img 
                            src={post.featuredImage} 
                            alt={post.title} 
                            className="w-full h-full object-contain"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-accent/20 text-primary">
                            <div className="text-center">
                                <Eye className="h-16 w-16 mx-auto mb-4 opacity-50" />
                                <p className="text-lg font-serif font-medium">Media Preview Unavailable</p>
                            </div>
                        </div>
                    )}
                </div>

                {}
                <h1 className="text-2xl md:text-4xl font-serif font-bold leading-tight mt-2 text-primary tracking-tight">
                    {post.title}
                </h1>

                {}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-border">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border border-border">
                            <AvatarImage src={post.author?.avatar?.["96"] || ""} />
                            <AvatarFallback className="bg-primary text-primary-foreground">A</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                            <Link to={post.author?.link || "#"} className="font-serif font-bold text-sm hover:text-secondary transition-colors text-primary">
                                {post.author?.name || "AOTO Strategic Advisor"}
                            </Link>
                            <span className="text-xs font-sans text-muted-foreground">125K Industry Partners</span>
                        </div>
                        <Button 
                            onClick={() => setIsSubscribed(!isSubscribed)}
                            variant={isSubscribed ? "secondary" : "default"}
                            className={`ml-4 rounded-sm px-6 font-sans font-semibold uppercase tracking-wider text-xs ${isSubscribed ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"}`}
                        >
                            {isSubscribed ? (
                                <>
                                    <Bell className="mr-2 h-4 w-4" />
                                    Following Updates
                                </>
                            ) : (
                                "Follow Insights"
                            )}
                        </Button>
                    </div>

                    <ActionButtons />
                </div>

                {}
                <div className="bg-muted/30 hover:bg-muted/50 transition-colors rounded-sm p-6 text-sm cursor-pointer group shadow-sm border border-border/50">
                    <div className="flex gap-2 font-serif font-bold mb-3 text-primary/80 uppercase tracking-widest text-xs">
                        <span>15,234 Executive Reads</span>
                        <span>•</span>
                        <span>{post.date ? new Date(post.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : "Recently Published"}</span>
                    </div>
                    <div 
                        className="prose prose-sm max-w-none text-foreground/90 font-sans leading-relaxed line-clamp-3 group-hover:line-clamp-none transition-all"
                        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "No detailed analysis available." }} 
                    />
                    <Button variant="ghost" className="mt-4 h-auto p-0 text-sm font-serif font-bold text-primary hover:text-secondary hover:bg-transparent">
                        Expand Analysis
                    </Button>
                </div>

                {}
                <div className="mt-8 hidden md:block">
                    <div className="flex items-center gap-8 mb-6 border-l-4 border-secondary pl-4">
                        <h3 className="text-xl font-serif font-bold text-primary tracking-tight">Executive Discussion</h3>
                        <Button variant="ghost" className="flex items-center gap-2 text-sm font-sans font-medium hover:bg-transparent hover:text-secondary px-0">
                            <Ellipsis className="h-5 w-5" />
                            Sort by Priority
                        </Button>
                    </div>
                    
                    <div className="flex gap-4 mb-8">
                        <Avatar className="h-10 w-10 border border-border">
                            <AvatarFallback className="bg-accent text-accent-foreground font-bold">ME</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                            <input 
                                type="text" 
                                placeholder="Post a professional inquiry..." 
                                className="w-full border-b border-border bg-transparent py-2 text-sm font-sans focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/60"
                            />
                            <div className="flex justify-end gap-2 mt-4">
                                <Button variant="ghost" size="sm" className="rounded-sm font-sans">Discard</Button>
                                <Button size="sm" className="rounded-sm font-sans bg-primary text-primary-foreground" disabled>Submit Inquiry</Button>
                            </div>
                        </div>
                    </div>

                    {}
                    <div className="space-y-8">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex gap-4" data-index={i}>
                                <Avatar className="h-10 w-10 border border-border">
                                    <AvatarFallback className="bg-muted text-muted-foreground">P{i}</AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-serif font-bold text-primary">Strategic Partner {i}</span>
                                        <span className="text-xs font-sans text-muted-foreground">2 days ago</span>
                                    </div>
                                    <p className="text-sm font-sans text-foreground/80 leading-relaxed">
                                        {i === 1 && "AOTO's approach to EPCO integration is a benchmark for international tourism development. The efficiency gains in procurement are significant."}
                                        {i === 2 && "The emphasis on Belt and Road synergies is particularly relevant for our current investment portfolio in the cultural sector."}
                                        {i === 3 && "Exceptional clarity on the design-build-operate transition. This provides the high-end assurance our investors require."}
                                    </p>
                                    <div className="flex items-center gap-4 mt-2">
                                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm hover:bg-accent">
                                            <ThumbsUp className="h-4 w-4" />
                                        </Button>
                                        <span className="text-xs font-sans text-muted-foreground">12</span>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm hover:bg-accent">
                                            <ThumbsDown className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-8 rounded-sm text-xs font-sans font-bold text-primary hover:text-secondary">Reply</Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {}
            <RelatedPostsSidebar currentPostId={post.id} />
        </div>
    );
}



export default function SinglePostTemplateSection() {
    
    
    const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
    
    
    const wp_query = new WP_Query(currentWPQueryParams);

    return (
        <section id="single-post-template-section" className="bg-background text-foreground w-full min-h-screen">
            <PostsProvider wp_query={wp_query}>
                <SinglePostConsumer />
            </PostsProvider>
        </section>
    );
}